<template>
      <v-form
        ref="form"
        v-model="formIsValid"
      >
        <v-card-title class="d-block pb-5">
          <v-row>
            <v-col
              cols="12"
            >
              <v-img
                :src="logoPictureSource"
                height="100"
              />
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-text>
          <div class="text-primary">
            {{ $t('views.anonymouspages.requestpasswordchangekeycomponent.zurcksetzenIhresPassworts') }}
          </div>
          <div class="pt-2">
            {{ $t('views.anonymouspages.requestpasswordchangekeycomponent.wennSieIhr') }}<br>
            {{ $t('views.anonymouspages.requestpasswordchangekeycomponent.hierKnnenSie') }}
          </div>
          <v-col cols="8">
            <v-text-field
              v-model="email"
              maxlength="200"
              :label="$t('views.anonymouspages.requestpasswordchangekeycomponent.bitteGebenSie')"
              :error-messages="emailErrorMessages"
              :rules="[validations.required($t('views.anonymouspages.requestpasswordchangekeycomponent.emailRequired')), validations.email($t('validations.email'))]"
            />
          </v-col>
          <v-col
            cols="12"
          >
            <test-question-captcha />
          </v-col>
          <v-btn
            :disabled="!formIsValid"
            block
            variant="flat"
            clase="text-none"
            color="#329c36"
            @click="requestPasswordChangeKey"
          >
            {{ requestPasswordButtonText }}
          </v-btn>
          <div class="text-primary pt-3">
            {{ $t('views.anonymouspages.requestpasswordchangekeycomponent.bitteBeachtenSie') }}
          </div>
          <div class="pt-2">
            {{ $t('views.anonymouspages.requestpasswordchangekeycomponent.sobaldSieEinen') }}
          </div>
        </v-card-text>
        <link-sent-dialog-component
          ref="successDialog"
        />
      </v-form>
</template>

<script>
import validations from '@/components/formUtils/Validations'
import PublicService from '@/services/PublicService'
import NotificationService from '@/services/NotificationService'
import LinkSentDialogComponent from './LinkSentDialogComponent'
import TestQuestionCaptcha from '@/components/TestQuestionCaptcha.vue'

export default {
  components: {
    LinkSentDialogComponent,
    TestQuestionCaptcha,
  },
  data () {
    return {
      validations,
      email: '',
      emailErrorMessages: [],
      requestPasswordButtonDisabled: true,
      formIsValid: false,
    }
  },
  computed: {
    requestPasswordButtonText () {
      return this.$t('requestLink')
    },
    logoPictureSource () {
      const locale = this.$root.$i18n.locale
      if (locale === 'pt' || locale === 'ptBR') { return require('@/assets/emcomyLogo-pt.webp') }
      return require('@/assets/emcomyLogo.svg')
    },
  },
  watch: {
    formIsValid () {
      if (this.email && this.email.trim().length) {
        // Check both pending and verified status
        Promise.all([
          PublicService.isEmailPending(this.email.trim()),
          PublicService.isEmailVerified(this.email.trim())
        ]).then(async ([isPending, isVerified]) => {
          // Check if account is recently created (both pending and verified)
          if (isPending.data && isVerified.data) {
            this.emailErrorMessages = []
            this.requestPasswordButtonDisabled = true
            this.emailErrorMessages.push(this.$t('views.anonymouspages.requestpasswordchangekeycomponent.accountRecentlyCreated'))
          } else if (isPending.data && !isVerified.data) {
            // Account is pending but not verified yet
            this.emailErrorMessages = []
            this.requestPasswordButtonDisabled = true
            this.emailErrorMessages.push(this.$t('views.anonymouspages.requestpasswordchangekeycomponent.emailNotification'))
          } else if (!isPending.data && isVerified.data && (await this.$refs.form.validate()).valid) {
            // Account is verified and not pending (normal case)
            this.emailErrorMessages = []
            this.formIsValid = true
          } else if (!isVerified.data && (await this.$refs.form.validate()).valid) {
            // Email not found
            this.emailErrorMessages = []
            this.formIsValid = false
            this.emailErrorMessages.push(this.$t('views.anonymouspages.requestpasswordchangekeycomponent.emailNotFound'))
          } else if (!(await this.$refs.form.validate()).valid) {
            // Form validation failed
            this.emailErrorMessages = []
            this.formIsValid = false
          }
        }).catch((error) => NotificationService.error(error.message))
      } else {
        this.formIsValid = true
        this.emailErrorMessages = []
      }
    },
    email (newEmail) {
      if (newEmail && newEmail.trim().length) {
        // Check both pending and verified status
        Promise.all([
          PublicService.isEmailPending(newEmail.trim()),
          PublicService.isEmailVerified(newEmail.trim())
        ]).then(async ([isPending, isVerified]) => {
          // Check if account is recently created (both pending and verified)
          if (isPending.data && isVerified.data) {
            this.emailErrorMessages = []
            this.requestPasswordButtonDisabled = true
            this.emailErrorMessages.push(this.$t('views.anonymouspages.requestpasswordchangekeycomponent.accountRecentlyCreated'))
          } else if (isPending.data && !isVerified.data) {
            // Account is pending but not verified yet
            this.emailErrorMessages = []
            this.requestPasswordButtonDisabled = true
            this.emailErrorMessages.push(this.$t('views.anonymouspages.requestpasswordchangekeycomponent.emailNotConfirmed'))
          } else if (!isPending.data && isVerified.data && (await this.$refs.form.validate()).valid) {
            // Account is verified and not pending (normal case)
            this.emailErrorMessages = []
            this.formIsValid = true
          } else if (!isVerified.data && (await this.$refs.form.validate()).valid) {
            // Email not found
            this.emailErrorMessages = []
            this.formIsValid = false
            this.emailErrorMessages.push(this.$t('views.anonymouspages.requestpasswordchangekeycomponent.emailNotFound'))
          } else if (!(await this.$refs.form.validate()).valid) {
            // Form validation failed
            this.emailErrorMessages = []
            this.formIsValid = false
          }
        }).catch((error) => NotificationService.error(error.message))
      } else {
        this.formIsValid = true
        this.emailErrorMessages = []
      }
    },
  },
  methods: {
    async requestPasswordChangeKey () {
      if ((await this.$refs.form.validate()).valid) {
        this.requestPasswordButtonDisabled = true
        this.requestPasswordButtonText = this.$t('pleaseWait')
        PublicService.requestPasswordChangeKey(this.email)
          .then((response) => {
            this.$emit('closeForm')
            this.$refs.successDialog.open()
          }).catch(error => {
          NotificationService.error(null, error)
        }).finally(() => {
          this.requestPasswordButtonDisabled = false
          this.requestPasswordButtonText = this.$t('requestLink')
        })
      }
    },
    resetForm(){
      this.emailErrorMessages = []
      this.$refs.form.reset()
    }
  },
}
</script>
